import { useMutation, useQuery } from '@apollo/client';
import {
  Company,
  ErrorResponse,
  GET_COMPANY,
  PATCH_COMPANY,
} from '@frontend/data-access';
import { App, Button, Form, Input, Spin } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';

interface FormValues {
  description: string;
}

function EditShortDescForm({
  companyId,
  onFinish,
  onCancel,
}: {
  companyId: number;
  onFinish: () => void;
  onCancel: () => void;
}) {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const { data, loading } = useQuery<{ company: Company }>(GET_COMPANY, {
    variables: { id: companyId },
  });
  const [isIncomplete, setIsIncomplete] = useState(false);
  const [form] = Form.useForm();

  const handleCompleted = () => {
    notification.success({
      message: t('success_message'),
    });
    onFinish();
  };

  const handleError = (data: ErrorResponse) => {
    notification.error({
      message: t('error_message'),
      description: data.message,
    });
  };

  const [patchCompany, { loading: loadingPatch }] = useMutation(PATCH_COMPANY, {
    onCompleted: handleCompleted,
    onError: handleError,
  });

  const handleOnFinish = (values: FormValues) => {
    patchCompany({
      variables: {
        input: {
          id: companyId,
          ...values,
        },
      },
    });
  };

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    setIsIncomplete(hasErrors);
  };

  useEffect(() => {
    if (data?.company) {
      form.setFieldsValue({ description: data?.company.description });
    }
  }, [data, form]);

  if (loading) {
    return (
      <div className="p-6 text-center">
        <Spin />
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleOnFinish}
      onFieldsChange={handleFormChange}
    >
      <Form.Item
        name="description"
        label={t('short_description_label')}
        rules={[{ required: true, message: t('description_required_message') }]}
      >
        <Input.TextArea
          rows={6}
          maxLength={1000}
          showCount
          placeholder={t('institution_short_description_placeholder')}
          data-test="institution-shortdescription-input"
        />
      </Form.Item>
      <div className="flex justify-end items-center gap-2">
        <Button disabled={loadingPatch} shape="round" onClick={onCancel}>
          {t('cancel_button')}
        </Button>
        <Button
          htmlType="submit"
          type="primary"
          loading={loadingPatch}
          disabled={isIncomplete}
          shape="round"
          data-test="short-description-save-btn"
        >
          {t('save_button')}
        </Button>
      </div>
    </Form>
  );
}

export default EditShortDescForm;
